import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, RotateCcw, LifeBuoy, User as UserIcon, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "--";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AssistantQueries() {
  const [queries, setQueries] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Open");
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assistant/queries`, { headers: getAuthHeaders() });
      setQueries(res.data.queries || []);
      setOpenCount(res.data.open_count || 0);
    } catch (err) {
      toast.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setStatus = async (q, action) => {
    setBusyId(q.id);
    try {
      await axios.post(`${API}/assistant/queries/${q.id}/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(action === "resolve" ? "Marked as resolved" : "Reopened");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = queries.filter((q) => q.status === tab);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="assistant-queries-page">
      <div className="flex items-start justify-between mb-5 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-[#E05A47]" /> Arjun Query Inbox
          </h1>
          <p className="text-sm text-stone-500 mt-1">Queries HR users raised to admin through the Arjun assistant.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="aq-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="aq-tabs">
          <TabsTrigger value="Open" data-testid="aq-tab-open">
            Open {openCount > 0 && <Badge className="ml-2 bg-[#E05A47] text-white">{openCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="Resolved" data-testid="aq-tab-resolved">Resolved</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-stone-400" data-testid="aq-empty">
          <LifeBuoy className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No {tab.toLowerCase()} queries.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((q) => {
            const askerName = (q.user_name || "User").split(" ")[0];
            const transcript = Array.isArray(q.transcript) ? q.transcript : [];
            const isOpen = !!expanded[q.id];
            return (
            <Card key={q.id} className="border-stone-200" data-testid={`aq-card-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-stone-700">
                      <UserIcon className="w-4 h-4 text-[#2E73B8]" /> {q.user_name || "Unknown user"}
                      <span className="text-xs font-normal text-stone-400">· {formatDate(q.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm text-stone-600 whitespace-pre-wrap bg-stone-50 border border-stone-100 rounded-lg px-3 py-2">{q.query}</p>
                    {transcript.length > 0 && (
                      <div className="mt-2">
                        <button
                          onClick={() => setExpanded((e) => ({ ...e, [q.id]: !e[q.id] }))}
                          className="flex items-center gap-1.5 text-xs font-medium text-[#2E73B8] hover:underline"
                          data-testid={`aq-transcript-toggle-${q.id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          {isOpen ? "Hide" : "View"} conversation ({transcript.length})
                          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                        {isOpen && (
                          <div className="mt-2 space-y-2 border-l-2 border-[#3DA635]/40 pl-3" data-testid={`aq-transcript-${q.id}`}>
                            {transcript.map((t, idx) => (
                              <div key={idx} className="text-xs">
                                <span className={`font-semibold ${t.role === "user" ? "text-[#2E73B8]" : "text-[#3DA635]"}`}>
                                  {t.role === "user" ? askerName : "Arjun"}:
                                </span>{" "}
                                <span className="text-stone-600 whitespace-pre-wrap">{t.content}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {q.status === "Resolved" && (
                      <p className="mt-2 text-xs text-[#3DA635]">Resolved by {q.resolved_by || "admin"} · {formatDate(q.resolved_at)}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {q.status === "Open" ? (
                      <Button size="sm" onClick={() => setStatus(q, "resolve")} disabled={busyId === q.id} className="bg-[#3DA635] hover:bg-[#2f8a2a]" data-testid={`aq-resolve-${q.id}`}>
                        {busyId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Resolve</>}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(q, "reopen")} disabled={busyId === q.id} data-testid={`aq-reopen-${q.id}`}>
                        {busyId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-1.5" /> Reopen</>}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
